/**
 * Deeplink / navigation-target validation.
 *
 * Any externally-supplied navigation target (push notifications, AppsFlyer
 * OneLinks, App Links, backend-provided links) must be validated before being
 * handed to the router, otherwise a poisoned value can redirect the in-app
 * WebView to an attacker-controlled origin (phishing) or an unintended route.
 *
 * The only safe targets are:
 *   - a relative in-app path ("/account", "/categories/passport?x=1"), or
 *   - an absolute URL whose host is one of our own website hosts.
 */

// Website hosts we own and register as App Links. Keep in sync with the
// Android assetlinks / iOS associated-domains configuration.
export const WEBSITE_APP_LINK_HOSTS = new Set([
  'docupro.in',
  'www.docupro.in',
  'testing.docupro.in',
]);

/**
 * Older console builds — and notifications already delivered before this fix —
 * emit deep links at top-level paths that don't exist in this app (the real
 * routes live under /account). Rewrite the known-legacy prefixes to their real
 * locations so those links resolve instead of 404-ing.
 *
 * Only rewrites paths that start with a legacy prefix as a full path segment,
 * so an already-correct "/account/requests/..." is left untouched.
 */
function normalizeLegacyPath(path: string): string {
  for (const prefix of ['/requests', '/profile']) {
    if (
      path === prefix ||
      path.startsWith(`${prefix}/`) ||
      path.startsWith(`${prefix}?`) ||
      path.startsWith(`${prefix}#`)
    ) {
      return `/account${path}`;
    }
  }
  return path;
}

/**
 * Normalizes an external navigation target to a safe relative in-app path, or
 * returns null if it isn't safe to navigate to.
 *
 * - Relative paths must start with a single "/" (rejects "//evil.com" and
 *   scheme-relative URLs, and "javascript:"/other schemes).
 * - Absolute URLs are accepted only when the host is in WEBSITE_APP_LINK_HOSTS,
 *   and are reduced to their path+query+hash.
 * - Known-legacy deep-link prefixes are rewritten to their real /account route.
 */
export function safeInAppPath(value: string | null | undefined): string | null {
  if (!value || typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  // Relative path: exactly one leading slash, not protocol-relative.
  if (trimmed.startsWith('/')) {
    return trimmed.startsWith('//') ? null : normalizeLegacyPath(trimmed);
  }

  // Absolute URL: only our own hosts, reduced to a relative path.
  try {
    const u = new URL(trimmed);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    if (!WEBSITE_APP_LINK_HOSTS.has(u.host)) return null;
    const path = `${u.pathname}${u.search}${u.hash}`;
    return path && path !== '/' ? normalizeLegacyPath(path) : '/';
  } catch {
    return null;
  }
}
